import type { LoaderFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import { z } from 'zod';

import type { CalendarAvailabilityData } from '~/modules-rocco/booking/api/get-calendar-availability.server';
import type {
    BookingRosterMember,
    RequestAvailabilityConfigSummary,
} from '~/modules-rocco/booking/api/get-request-availability.server';
import { getRequestAvailability } from '~/modules-rocco/booking/api/get-request-availability.server';

export const BOOKING_REQUEST_AVAILABILITY_API_ROUTE =
    '/remix-api/booking/request-availability';

interface RequestAvailabilityError {
    code: 'notFound' | 'loadFailed' | 'enquiriesClosed';
    message: string;
}

export interface BookingRequestAvailabilityApiResponse {
    availability?: CalendarAvailabilityData;
    /**
     * The booking link the offered week was read from (or `source: 'default'`
     * for the plain working week), with the details the gate presents.
     */
    requestConfig?: RequestAvailabilityConfigSummary;
    /** Everyone the practice shows, and how a meeting with each is made. */
    roster?: BookingRosterMember[];
    /** False when only calendar-backed members are offerable. */
    allowsEnquiries?: boolean;
    error?: RequestAvailabilityError;
}

const QUERY_SCHEMA = z.object({
    professional: z.string().min(1),
    // Booking link whose working hours to offer: the one a form routed to, or
    // the one a CTA was configured with. Ignored unless it is this
    // professional's and switched on.
    sessionId: z.coerce.number().int().positive().optional(),
});

const failure = (error: RequestAvailabilityError) =>
    routerData<BookingRequestAvailabilityApiResponse>(
        { error },
        { status: error.code === 'notFound' ? 404 : 400 },
    );

/**
 * Working-week times for a professional with no connected calendar, in the
 * shape the booking card already renders. The hours come from the booking link
 * named by `sessionId`, else the professional's own bookable link, else a plain
 * Monday-to-Friday week.
 *
 * Deliberately separate from the session availability route: there is no
 * session to disable, no calendar connection to check, and nothing to fetch
 * from a calendar provider. Submitting one of these goes to
 * {@link BOOKING_REQUEST_API_ROUTE}, not the booking mutation.
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const parsed = QUERY_SCHEMA.safeParse({
        professional: url.searchParams.get('professional'),
        sessionId: url.searchParams.get('sessionId') || undefined,
    });

    if (!parsed.success) {
        return routerData<BookingRequestAvailabilityApiResponse>(
            {
                error: {
                    code: 'notFound',
                    message: 'A professional is required.',
                },
            },
            { status: 400 },
        );
    }

    const { professional, sessionId } = parsed.data;
    const result = await getRequestAvailability(professional, context, {
        sessionId,
    });

    if (!result.success) {
        return failure(result.error);
    }

    return routerData<BookingRequestAvailabilityApiResponse>({
        availability: result.data,
        requestConfig: result.config,
        roster: result.roster,
        allowsEnquiries: result.allowsEnquiries,
    });
};
